import * as React from "react"
import { toZonedTime } from "date-fns-tz"

import { Button, Label, Select } from "@ibada/ui"

import { useLocale, useTranslations } from "@/i18n"
import { useActiveBranchTimezone } from "@/features/auth/hooks/use-active-branch-timezone"
import { FinancePeriodPicker } from "@/features/finance/components/finance-period-picker"
import { resolvePeriodRange } from "@/shared/lib/dates/period-range"
import { useIsMobile } from "@/shared/hooks/use-mobile"
import { MobileFormScreen, MobileFormSection, MobileFormSelectRow } from "@/shared/components/mobile-form"
import { useAccountingChartQuery, useFinanceActivitiesQuery, useFinanceFundsQuery } from "@/features/finance/services/finance-query"
import { PeriodPreset } from "@/shared/lib/dates/period-range"

import {
  ReportFieldKind,
  type FinanceReportEntry,
  type ReportFieldSpec,
} from "@/features/reports/model/report-registry-types"
import { isConfigComplete, type ReportConfigValues } from "@/features/reports/lib/report-config-url"
import { useFinanceReportScope } from "@/features/reports/services/use-finance-report-scope"

/**
 * A minimal config form driven by the report's `configSchema.fields` via a fixed renderer switch — NOT a
 * generic form builder. Reuses `FinancePeriodPicker` and the accounting chart for the account picker.
 * (P2 covers period + account; the remaining field kinds land with the finance-resource reports.)
 */
/** Phone: the finance period presets offered by the desktop picker, in its order. */
const MOBILE_PERIOD_PRESETS: readonly PeriodPreset[] = [
  PeriodPreset.ThisMonth,
  PeriodPreset.ThisQuarter,
  PeriodPreset.ThisYear,
  PeriodPreset.LastMonth,
  PeriodPreset.LastQuarter,
  PeriodPreset.LastYear,
  PeriodPreset.MonthToDate,
  PeriodPreset.QuarterToDate,
  PeriodPreset.YearToDate,
]

/** The props every phone field row shares: its label and the picker sheet's chrome. */
type MobileFieldChrome = { label: string; doneLabel: string; emptyLabel: string }

export function ReportConfigForm({
  entry,
  onSubmit,
  initialValues,
  mobileTitle,
  onCancel,
}: {
  entry: FinanceReportEntry
  onSubmit: (values: ReportConfigValues) => void
  /** Seed the form from an existing configuration (e.g. reconfiguring from the report view). */
  initialValues?: ReportConfigValues
  /** Phone: the screen's title; the form then renders as the app's full-screen form. */
  mobileTitle?: string
  /** Phone: the top bar's Annuler. */
  onCancel?: () => void
}) {
  const t = useTranslations()
  const tMobile = useTranslations("common.mobileForm")
  const isMobile = useIsMobile()
  const fields = entry.configSchema?.fields ?? []

  const [values, setValues] = React.useState<ReportConfigValues>(() => {
    const initial: ReportConfigValues = {}
    for (const field of fields) {
      if (field.kind === ReportFieldKind.Period) initial[field.key] = field.defaultPreset ?? PeriodPreset.MonthToDate
    }
    return { ...initial, ...(initialValues ?? {}) }
  })

  const setValue = (key: string, value: string) => setValues((prev) => ({ ...prev, [key]: value }))

  if (isMobile && mobileTitle && onCancel) {
    // Phone: the app's full-screen form; each option is a row opening a picker sheet, Generate pinned below.
    return (
      <MobileFormScreen
        title={mobileTitle}
        cancelLabel={t("finance.reports.saveDialog.actions.cancel" as never)}
        submitLabel={t("finance.reports.config.generate" as never)}
        submitDisabled={!isConfigComplete(values, entry.configSchema)}
        onSubmit={() => onSubmit(values)}
        onCancel={onCancel}
      >
        <MobileFormSection footer={t("finance.reports.config.subtitle" as never)}>
          {fields.map((field) => (
            <ReportField
              key={field.key}
              field={field}
              value={values[field.key] ?? ""}
              onChange={(value) => setValue(field.key, value)}
              mobile={{ label: t(field.labelKey as never), doneLabel: tMobile("done"), emptyLabel: tMobile("noResults") }}
            />
          ))}
        </MobileFormSection>
      </MobileFormScreen>
    )
  }

  return (
    <div className="max-w-md space-y-4">
      {fields.map((field) => (
        <div key={field.key} className="space-y-1.5">
          <Label>{t(field.labelKey as never)}</Label>
          <ReportField field={field} value={values[field.key] ?? ""} onChange={(value) => setValue(field.key, value)} />
        </div>
      ))}

      <Button onClick={() => onSubmit(values)} disabled={!isConfigComplete(values, entry.configSchema)}>
        {t("finance.reports.config.generate" as never)}
      </Button>
    </div>
  )
}

function ReportField({
  field,
  value,
  onChange,
  mobile,
}: {
  field: ReportFieldSpec
  value: string
  onChange: (value: string) => void
  /** Phone: render the field as a row of a `MobileFormSection`. */
  mobile?: MobileFieldChrome
}) {
  const t = useTranslations()

  if (field.kind === ReportFieldKind.Period) {
    if (mobile) {
      return <MobilePeriodRow chrome={mobile} value={(value || field.defaultPreset || PeriodPreset.MonthToDate) as PeriodPreset} onChange={onChange} />
    }
    return (
      <FinancePeriodPicker
        value={(value || field.defaultPreset || PeriodPreset.MonthToDate) as PeriodPreset}
        onValueChange={(preset) => onChange(preset)}
      />
    )
  }

  if (field.kind === ReportFieldKind.Account) {
    return <AccountField value={value} onChange={onChange} mobile={mobile} />
  }

  if (field.kind === ReportFieldKind.Fund) {
    return <FundField value={value} onChange={onChange} mobile={mobile} />
  }

  if (field.kind === ReportFieldKind.Activity) {
    return <ActivityField value={value} onChange={onChange} mobile={mobile} />
  }

  // Remaining field kinds (month/date/branch/lookup) arrive with their reports.
  if (mobile) {
    return <p className="px-4 py-3.5 text-sm font-semibold text-muted-foreground">{t("finance.reports.config.unsupportedField" as never)}</p>
  }
  return <p className="text-xs text-muted-foreground">{t("finance.reports.config.unsupportedField" as never)}</p>
}

function AccountField({ value, onChange, mobile }: { value: string; onChange: (value: string) => void; mobile?: MobileFieldChrome }) {
  const t = useTranslations()
  const scope = useFinanceReportScope()
  const query = useAccountingChartQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId, scope: scope.scope } : null,
  )
  const items = query.data?.items ?? []
  // Names arrive localized by the API (ADR-0030), seeded accounts included.
  if (mobile) {
    return (
      <MobileFormSelectRow
        label={mobile.label}
        doneLabel={mobile.doneLabel}
        emptyLabel={mobile.emptyLabel}
        placeholder={t("finance.reports.config.selectAccount" as never)}
        value={value || undefined}
        onChange={(next) => onChange(next ?? "")}
        options={items.map((account) => ({
        value: account.id,
        label: `${account.code} · ${account.name}`,
      }))}
      />
    )
  }
  return (
    <Select
      value={value || undefined}
      onValueChange={onChange}
      options={items.map((account) => ({
        value: account.id,
        label: `${account.code} · ${account.name}`,
      }))}
      placeholder={t("finance.reports.config.selectAccount" as never)}
    />
  )
}

function FundField({ value, onChange, mobile }: { value: string; onChange: (value: string) => void; mobile?: MobileFieldChrome }) {
  const t = useTranslations()
  const scope = useFinanceReportScope()
  const query = useFinanceFundsQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId, scope: scope.scope } : null,
  )
  const items = query.data?.items ?? []
  if (mobile) {
    return (
      <MobileFormSelectRow
        label={mobile.label}
        doneLabel={mobile.doneLabel}
        emptyLabel={mobile.emptyLabel}
        placeholder={t("finance.reports.config.selectFund" as never)}
        value={value || undefined}
        onChange={(next) => onChange(next ?? "")}
        options={items.map((fund) => ({ value: fund.id, label: fund.name }))}
      />
    )
  }
  return (
    <Select
      value={value || undefined}
      onValueChange={onChange}
      options={items.map((fund) => ({ value: fund.id, label: fund.name }))}
      placeholder={t("finance.reports.config.selectFund" as never)}
    />
  )
}

function ActivityField({ value, onChange, mobile }: { value: string; onChange: (value: string) => void; mobile?: MobileFieldChrome }) {
  const t = useTranslations()
  const scope = useFinanceReportScope()
  const query = useFinanceActivitiesQuery(
    scope.workspaceId ? { workspaceId: scope.workspaceId, branchId: scope.branchId ?? undefined } : null,
  )
  const items = query.data ?? []
  if (mobile) {
    return (
      <MobileFormSelectRow
        label={mobile.label}
        doneLabel={mobile.doneLabel}
        emptyLabel={mobile.emptyLabel}
        placeholder={t("finance.reports.config.selectActivity" as never)}
        value={value || undefined}
        onChange={(next) => onChange(next ?? "")}
        options={items.map((activity) => ({ value: activity.id, label: activity.title }))}
      />
    )
  }
  return (
    <Select
      value={value || undefined}
      onValueChange={onChange}
      options={items.map((activity) => ({ value: activity.id, label: activity.title }))}
      placeholder={t("finance.reports.config.selectActivity" as never)}
    />
  )
}

/** Phone: the period as a row; its sheet lists the presets with the dates each one covers today. */
function MobilePeriodRow({ chrome, value, onChange }: { chrome: MobileFieldChrome; value: PeriodPreset; onChange: (value: string) => void }) {
  const t = useTranslations("finance.period")
  const locale = useLocale()
  const timeZone = useActiveBranchTimezone()
  const options = React.useMemo(() => {
    const zonedNow = toZonedTime(new Date(), timeZone)
    const day = new Intl.DateTimeFormat(locale, { day: "numeric", month: "short", year: "numeric" })
    return MOBILE_PERIOD_PRESETS.map((preset) => {
      const range = resolvePeriodRange(preset, { now: zonedNow, weekStartsOn: 1 })
      return {
        value: preset,
        label: t(`presets.${preset}` as never),
        description: range ? `${day.format(range.start)} - ${day.format(range.end)}` : undefined,
      }
    })
  }, [locale, t, timeZone])
  // The current value always shows, even a preset this list does not offer (a deep link from elsewhere).
  const valueLabel = options.find((option) => option.value === value)?.label ?? t(`presets.${value}` as never)
  return (
    <MobileFormSelectRow
      label={chrome.label}
      doneLabel={chrome.doneLabel}
      emptyLabel={chrome.emptyLabel}
      placeholder={t("label")}
      value={value}
      valueLabel={valueLabel}
      onChange={(next) => next && onChange(next)}
      options={options}
    />
  )
}
